// src/layouts/AddTeammateModal.tsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  MenuItem,
  IconButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  alpha,
  useTheme,
  type SxProps,
  type Theme,
} from "@mui/material/styles";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { MuiTelInput } from "mui-tel-input";
import { PHONE_CONFIG } from "../../constants";
import { createPhoneNumberHandler } from "../../utils/phoneNumberInputHandler";
import { normalizePhoneNumberForParsing } from "../../utils/formatPhoneNumber";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import logger from "../../utils/logger";
import { useDashboardTheme } from "../Dashboard/context/DashboardThemeContext";

// 1) validation schema with inline i18n messages
export const AddTeammateSchema = (
  t: (key: string) => string,
  isEdit = false,
) => {
  const base: Record<string, any> = {
    firstname: yup.string().required(t("addTeammate.error.firstname")),
    lastname: yup.string().required(t("addTeammate.error.lastname")),
    email: yup
      .string()
      .email(t("addTeammate.error.email"))
      .required(t("addTeammate.error.email")),
    telephone: yup.string().required(t("addTeammate.error.telephone")),
    role: yup
      .string()
      .oneOf(["ADMIN", "BASIC"], t("addTeammate.error.role"))
      .required(t("addTeammate.error.role")),
    gender: yup
      .string()
      .oneOf(["male", "female", "other"], t("addTeammate.error.gender"))
      .required(t("addTeammate.error.gender")),
    language: yup.string().required(t("addTeammate.error.language")),
  };

  if (!isEdit) {
    Object.assign(base, {
      password: yup
        .string()
        .min(6, t("addTeammate.error.password"))
        .required(t("addTeammate.error.password")),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref("password")], t("addTeammate.error.confirmPassword"))
        .required(t("addTeammate.error.confirmPassword")),
    });
  }

  return yup.object(base).required();
};

type FormValues = yup.InferType<ReturnType<typeof AddTeammateSchema>>;

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  initialData?: Partial<FormValues>;
  companyId: string;
}

export default function AddTeammateModal({
  open,
  onClose,
  onSave,
  initialData,
  companyId,
}: Props) {
  const { t } = useTranslation();
  const isEdit = Boolean(initialData);
  const theme = useTheme();
  const isDark = useDashboardTheme();

  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const defaultVals: any = React.useMemo(() => {
    // Normalize phone number if it comes from backend without "+"
    let normalizedTelephone = "";
    if (initialData?.telephone) {
      const normalized = normalizePhoneNumberForParsing(initialData.telephone);
      const parsed = parsePhoneNumberFromString(normalized);
      normalizedTelephone = parsed?.number || initialData.telephone;
    }

    return {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "BASIC",
      gender: "male",
      language: "Brazilian Portuguese",
      ...initialData,
      telephone: normalizedTelephone || "",
    };
  }, [initialData]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(AddTeammateSchema(t, isEdit)),
    defaultValues: defaultVals,
  });

  // always reset on edit vs. new
  useEffect(() => {
    reset(defaultVals);
  }, [initialData, defaultVals, reset]);

  const onSubmit: SubmitHandler<FormValues> = async (data: any) => {
    setLoading(true);

    try {
      // Clean telephone: remove non-digit chars and remove leading country code +55 if present
      const cleanedTelephone = data.telephone.replace(/\D/g, "");

      const { confirmPassword, ...userData } = {
        ...data,
        telephone: cleanedTelephone,
      };
      await onSave(userData);

      onClose();
    } catch (error) {
      logger.error("Error when creating user:", error);
    } finally {
      setLoading(false);
    }
  };
  const darkFieldSx: SxProps<Theme> = isDark
    ? {
        "& .MuiInputBase-root": {
          color: "rgba(255,255,255,0.87)",
          backgroundColor: "rgba(255,255,255,0.05)",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.2)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.4)",
        },
        "& .MuiInputLabel-root": {
          color: "rgba(255,255,255,0.6)",
        },
        "& .MuiSvgIcon-root": {
          color: "rgba(255,255,255,0.6)",
        },
      }
    : {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: isDark ? "#1e1e1e" : "#ffffff",
          backgroundImage: "none",
          border: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: isDark ? "rgba(255,255,255,0.92)" : "inherit",
          borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined,
        }}
      >
        {initialData ? t("addTeammate.editTitle") : t("addTeammate.addTitle")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: isDark ? "rgba(255,255,255,0.6)" : undefined,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          borderColor: isDark ? "rgba(255,255,255,0.08)" : undefined,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            mt: 1,
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          {(["firstname", "lastname", "email", "telephone"] as const).map(
            (field) => (
              <Controller
                key={field}
                name={field}
                control={control}
                render={({ field: f }) =>
                  field === "telephone" ? (
                    <MuiTelInput
                      value={f.value || ""}
                      onChange={createPhoneNumberHandler(f.onChange, f.value)}
                      defaultCountry={PHONE_CONFIG.DEFAULT_COUNTRY}
                      label={t(`addTeammate.field.${field}`)}
                      forceCallingCode
                      fullWidth
                      error={!!errors[field]}
                      helperText={errors[field]?.message as React.ReactNode}
                      size="small"
                      sx={darkFieldSx}
                    />
                  ) : (
                    <TextField
                      {...f}
                      label={t(`addTeammate.field.${field}`)}
                      type={field === "email" ? "email" : "text"}
                      fullWidth
                      size="small"
                      variant="outlined"
                      error={!!errors[field]}
                      helperText={errors[field]?.message as React.ReactNode}
                      sx={darkFieldSx}
                    />
                  )
                }
              />
            ),
          )}

          {/* Role */}
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label={t("addTeammate.field.role")}
                fullWidth
                size="small"
                variant="outlined"
                error={!!errors.role}
                helperText={errors.role?.message as React.ReactNode}
                sx={darkFieldSx}
              >
                <MenuItem value="ADMIN">{t("addTeammate.role.admin")}</MenuItem>
                <MenuItem value="BASIC">
                  {t("addTeammate.role.employee")}
                </MenuItem>
              </TextField>
            )}
          />

          {/* Gender */}
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label={t("addTeammate.field.gender")}
                fullWidth
                size="small"
                variant="outlined"
                error={!!errors.gender}
                helperText={errors.gender?.message as React.ReactNode}
                sx={darkFieldSx}
              >
                <MenuItem value="male">{t("addTeammate.gender.male")}</MenuItem>
                <MenuItem value="female">
                  {t("addTeammate.gender.female")}
                </MenuItem>
                <MenuItem value="other">
                  {t("addTeammate.gender.other")}
                </MenuItem>
              </TextField>
            )}
          />

          {/* Language */}
          <Controller
            name="language"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label={t("addTeammate.field.language")}
                fullWidth
                size="small"
                variant="outlined"
                error={!!errors.language}
                helperText={errors.language?.message as React.ReactNode}
                sx={darkFieldSx}
              >
                <MenuItem value="Brazilian Portuguese">
                  {t("addTeammate.language.pt")}
                </MenuItem>
                <MenuItem value="English">
                  {t("addTeammate.language.en")}
                </MenuItem>
                <MenuItem value="Spanish">
                  {t("addTeammate.language.es")}
                </MenuItem>
              </TextField>
            )}
          />

          {/* Password */}
          {!isEdit && (
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type={showPw ? "text" : "password"}
                  label={t("addTeammate.field.password")}
                  fullWidth
                  size="small"
                  variant="outlined"
                  error={!!errors.password}
                  helperText={errors.password?.message as React.ReactNode}
                  sx={darkFieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPw((v) => !v)}
                          edge="end"
                        >
                          {showPw ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          )}

          {/* Confirm Password */}
          {!isEdit && (
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type={showConfirm ? "text" : "password"}
                  label={t("addTeammate.field.confirmPassword")}
                  fullWidth
                  size="small"
                  variant="outlined"
                  error={!!errors.confirmPassword}
                  helperText={
                    errors.confirmPassword?.message as React.ReactNode
                  }
                  sx={darkFieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirm((v) => !v)}
                          edge="end"
                        >
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined,
        }}
      >
        <Button
          onClick={onClose}
          sx={{ color: isDark ? "rgba(255,255,255,0.7)" : undefined }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSubmitting || loading}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 1),
            color: "#fff",
            "&:hover": {
              bgcolor: alpha(theme.palette.tertiary.main, 1),
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : initialData ? (
            t("common.save")
          ) : (
            t("common.create")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
